import { Labels } from '../../global/labels';
import { UIDateElement } from './ui-dateelement';
import {
    updateElement,
    createElement,
    insertElements,
    detachChildNodes,
    updateClassList,
    TimeInSeconds,
} from '../../global/ui-helpers';
import { getDaysInMonth } from '../../global/helpers';
import { Digest } from '../../global/digest';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type { ElementConfig, ObservedAttributes } from '../ui-element.type';
import type { NullOr, Optional, UndefinedOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { UIDatePicker } from './ui-datepicker';
import type {
    UIDateRangeCloudLinkType,
    UIDateRangeCloudLinksTypes,
} from './ui-daterange.type';
import type { UIField } from '../field/ui-field';
import type { UITimePicker } from './ui-timepicker';
import styles from './ui-daterange.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UIDateRange
 * @element ui-daterange
 * @classdesc Represents a class for <code>ui-daterange</code> element.
 * Uses 2 ui-datepickers to pick date range.
 * You can defined &lt;ui-datalist> with &tl;ui-option value=""> elements to make predefined
 * cloud links.
 * Possible values are:
 * TODAY - both datepickers are equal;
 * YESTERDAY - range between yesterday and today;
 * CURRENT_WEEK - range between beginning of the current week and today's date;
 * CURRENT_MONTH - range between beginning of the current month and today's date;
 * CURRENT_MONTH - range between beginning of the current year and today's date;
 * PREV_YEAR - range of last year;
 * CURRENT_YEAR - range of current year from 1st of January until current date;
 * PREV_WEEK - range of previous week;
 * PREV_MONTH - range of previous month;
 * PREV_MONTH_TODAY - range between previous month and today's date.
 * @property {string} [controlId] {@attr control-id} Base id for the date range group and its datepickers.
 *
 * Host element id resolution:
 * - If you set id directly on <ui-daterange>, it is preserved and used as the base for deriving child ids.
 *   Note: This only affects inputs that don't already have a user-set id. Existing user-set input ids
 *   are never overwritten.
 * - If no host id is set and control-id is provided, the host gets control-id.
 * - If no host id is set and no control-id is provided, a base id is generated.
 *
 * Child datepicker input id resolution:
 * - User-set ids are never overwritten. If you set an id on an input (e.g. id="daterange-from"),
 *   that value is preserved, regardless of whether a host id or control-id is set.
 * - If an input has no id, a derived id is applied: first picker `${baseId}-from`, second `${baseId}-to`,
 *   where baseId is the host's id (user-set, control-id, or generated).
 * - The group element always gets an id (user-set, control-id, or generated) so that aria-labelledby
 *   works correctly when the component is used inside a ui-field.
 * @property {boolean} allowEmpty {@attr allow-empty} If true, empty values won't be replaced
 * with default date, or date from another picker.
 * @property {UIDatePicker} pickerFrom Shortcut to datepicker-from element.
 * @property {UIDatePicker} pickerTo Shortcut to datepicker-to element.
 * @property {Array<HTMLButtonElement>} cloudLinks Shortcut to the array of links.
 * @property {Function} cloudLinkCallback Callback when links from clouds is clicked.
 * @slot
 * @example
 * <ui-daterange min="20.02.2019" max="25.02.2019">
 *   <ui-datepicker>
 *     <input name="dateFrom" value="21.02.2019" type="text">
 *   </ui-datepicker>
 *   <ui-datepicker>
 *     <input name="dateTo" value="23.02.2019" type="text">
 *   </ui-datepicker>
 *   <ui-datalist>
 *     <ui-option value="TODAY"></ui-option>
 *     <ui-option value="CURRENT_WEEK"></ui-option>
 *     <ui-option value="CURRENT_MONTH"></ui-option>
 *     <ui-option value="CURRENT_YEAR"></ui-option>
 *   </ui-datalist>
 * </ui-daterange>
 */
@Component({ tag: 'ui-daterange', styles })
export class UIDateRange extends UIDateElement {
    @Attribute(String) controlId?: string;
    @Attribute(Boolean) allowEmpty?: boolean;

    @Child('ui-datepicker.-from') pickerFrom?: UIDatePicker;
    @Child('ui-datepicker.-to') pickerTo?: UIDatePicker;
    @Children('.ui-daterange__cloud-link button')
    cloudLinks!: NodeListOf<HTMLButtonElement>;

    cloudLinkCallback?: () => void;

    private _fromInputHadUserId = false;
    private _toInputHadUserId = false;

    /**
     * @type {UIDateRange}
     */
    override readonly control: this = this;

    /**
     * @type {number}
     * @readonly
     */
    get tsFrom(): number {
        return this.dateStringToTimestamp(this.pickerFrom?.value ?? '');
    }

    /**
     * @type {number}
     * @readonly
     */
    get tsTo(): number {
        return this.dateStringToTimestamp(this.pickerTo?.value ?? '');
    }

    /**
     * @type {boolean}
     * @readonly
     */
    get shouldSync(): boolean {
        return !!(
            this.pickerTo?.control?.disabled ||
            this.pickerTo?.control?.readOnly ||
            this.pickerFrom?.control?.disabled ||
            this.pickerFrom?.control?.readOnly
        );
    }

    /**
     *
     * @returns {object}
     */
    static readonly cloudLinksTypes: UIDateRangeCloudLinksTypes = {
        TODAY: 'TODAY',
        YESTERDAY: 'YESTERDAY',
        CURRENT_WEEK: 'CURRENT_WEEK',
        CURRENT_MONTH: 'CURRENT_MONTH',
        CURRENT_YEAR: 'CURRENT_YEAR',
        PREV_YEAR: 'PREV_YEAR',
        PREV_WEEK: 'PREV_WEEK',
        PREV_MONTH: 'PREV_MONTH',
        PREV_MONTH_TODAY: 'PREV_MONTH_TODAY',
    } as const;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-daterange', {
            TODAY: 'Today',
            YESTERDAY: 'Yesterday',
            CURRENT_WEEK: 'Current week',
            CURRENT_MONTH: 'Current month',
            CURRENT_YEAR: 'Year to date',
            PREV_YEAR: 'Last year',
            PREV_WEEK: 'Last week',
            PREV_MONTH: 'Previous month',
            PREV_MONTH_TODAY: 'Last month and this month',
            PERIOD_START: 'Period start',
            PERIOD_END: 'Period end',
            TIME_START: 'Time start',
            TIME_END: 'Time end',
            CHOOSE_DATE_START: 'Choose start date',
            CHOOSE_DATE_END: 'Choose end date',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['control-id'];
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(name: string): void {
        switch (name) {
            case 'control-id':
                if (this.rendered) {
                    this.updateDerivedIds();
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Syncs start and end values.
     * @override
     */
    override syncControls(): void {
        if (!this.shouldSync) {
            return;
        }
        const pickerFrom = this.pickerFrom;
        const pickerTo = this.pickerTo;
        if (!pickerFrom || !pickerTo) {
            return;
        }
        if (!this.isValidDate(pickerTo.value)) {
            pickerFrom.max = this.max;
        } else {
            pickerFrom.max = pickerTo.value;
        }

        if (!this.isValidDate(pickerFrom.value)) {
            pickerTo.min = this.min;
        } else {
            pickerTo.min = pickerFrom.value;
        }
    }

    /**
     * Fires callback when a value of the date-from control is changed.
     * @private
     */
    private handleChangeDateFrom = (): void => {
        this.syncControls();
        if (this.pickerFrom && this.pickerTo) {
            this.enforceDateRangeRules(this.pickerFrom, this.pickerTo);
        }
    };

    /**
     * Fires callback when a value of the date-to control is changed.
     * @private
     */
    private handleChangeDateTo = (): void => {
        this.syncControls();
        if (this.pickerFrom && this.pickerTo) {
            this.enforceDateRangeRules(this.pickerTo, this.pickerFrom);
        }
    };

    /**
     * Enforces date range rules.
     *
     * Rules:
     * - The from-date must be before to-date
     * - The dates must be within min and max boundaries
     *
     * Exceptions:
     * - The targetPicker's date is empty or invalid - user might be typing
     * - When the otherPicker is not editable, the targetPicker's value
     * is adjusted instead of the targetPicker's value
     * - Allow otherPicker's value to be empty when 'allow-empty' is present
     * @param {UIDatePicker} targetPicker
     * @param {UIDatePicker} otherPicker
     * @private
     */
    private enforceDateRangeRules(
        targetPicker: UIDatePicker,
        otherPicker: UIDatePicker
    ): void {
        const [target, other] = [targetPicker, otherPicker].map((picker) => {
            return {
                timestamp: this.dateStringToTimestamp(picker.value ?? ''),
            };
        });

        const isTargetDateValid = targetPicker.valueAsDate != null;

        const isStartAfterEnd =
            targetPicker === this.pickerFrom
                ? target.timestamp > other.timestamp
                : target.timestamp < other.timestamp;

        if (!isTargetDateValid || !isStartAfterEnd) {
            return;
        }

        if (!otherPicker.isEditable) {
            targetPicker.value = otherPicker.value;
        } else if (!(this.allowEmpty && otherPicker.isEmpty)) {
            const { value: clampedValue } = this.parseDateValue(
                targetPicker.value ?? '',
                this.min,
                this.max
            );

            otherPicker.value = clampedValue;
            otherPicker.updateButtons();
        }
    }

    /**
     * Gets the cloud links options.
     * @returns {Array<string>}
     */
    getCloudLinksTypes(): Array<string> {
        return Array.from(this.querySelectorAll('ui-datalist ui-option')).map(
            (opt) => opt.getAttribute('value') || ''
        );
    }

    /**
     * Builds cloud links.
     * @param {Array<string>} linkTypes
     * @returns {HTMLDivElement}
     */
    buildCloudLinks(linkTypes: Array<string>): HTMLDivElement {
        return createElement<HTMLDivElement>({
            tagName: 'div',
            classList: {
                'ui-daterange__cloud': true,
            },
            children: [
                {
                    tagName: 'ul',
                    children: linkTypes.map(
                        (p): ElementConfig => ({
                            tagName: 'li',
                            classList: {
                                'ui-daterange__cloud-link': true,
                            },
                            children: [
                                {
                                    tagName: 'button',
                                    classList: {
                                        '-link': true,
                                    },
                                    attributes: {
                                        'type': 'button',
                                        'data-type': p,
                                    },
                                    children: [UIDateRange.labels[p]],
                                },
                            ],
                        })
                    ),
                },
            ],
        })!;
    }

    /**
     * Handle cloud link click and sets values to ui-dateranges.
     * @param {Event} e
     */
    private handleCloudLinkClick = (e: Event): void => {
        const target = e.target as NullOr<HTMLElement>;
        const type = target?.dataset.type as Optional<UIDateRangeCloudLinkType>;
        if (!type) {
            return;
        }
        this.resolvePredefinedValue(type);
    };

    /**
     * Resolves correct dates based on typings.
     * @param {string} type
     */
    resolvePredefinedValue(type: UIDateRangeCloudLinkType): void {
        if (!this.pickerFrom || !this.pickerTo) {
            return;
        }

        const todayDate = new Date();
        const todayTs = todayDate.getTime();
        const prevMonthDate = new Date(
            todayDate.getFullYear(),
            todayDate.getMonth() - 1,
            1
        );
        const DAY = TimeInSeconds.Day * 1000;
        const tzOffset = prevMonthDate.getTimezoneOffset() * 60000;

        // Don't remove might be useful.
        // const min = this.pickerFrom.dateStringToTimestamp(this.min);
        // const max = this.pickerFrom.dateStringToTimestamp(this.max);

        const lastYear = UIDateRange.getLastYearPeriod(todayDate);

        this.pickerTo.value = this.pickerTo.timestampToDateString(todayTs);
        switch (type) {
            case UIDateRange.cloudLinksTypes.TODAY:
                this.setPredefinedMinValue(todayTs);
                break;
            case UIDateRange.cloudLinksTypes.YESTERDAY:
                this.setPredefinedMinValue(todayTs - DAY);
                this.setPredefinedMaxValue(todayTs - DAY);
                break;
            case UIDateRange.cloudLinksTypes.CURRENT_WEEK:
                this.setPredefinedMinValue(
                    UIDateRange.getMonday(todayDate).getTime()
                );
                break;
            case UIDateRange.cloudLinksTypes.CURRENT_MONTH:
                this.setPredefinedMinValue(
                    todayTs - DAY * (todayDate.getDate() - 1)
                );
                break;
            case UIDateRange.cloudLinksTypes.CURRENT_YEAR:
                this.setPredefinedMinValue(
                    new Date(todayDate.getFullYear(), 0, 1).getTime()
                );
                break;
            case UIDateRange.cloudLinksTypes.PREV_YEAR:
                this.setPredefinedMinValue(lastYear.start.getTime());
                this.setPredefinedMaxValue(lastYear.end.getTime());
                break;
            case UIDateRange.cloudLinksTypes.PREV_WEEK:
                this.setPredefinedMinValue(
                    todayTs - DAY * (6 + todayDate.getDay())
                );
                this.setPredefinedMaxValue(todayTs - DAY * todayDate.getDay());
                break;
            case UIDateRange.cloudLinksTypes.PREV_MONTH:
                this.setPredefinedMinValue(prevMonthDate.getTime());
                this.setPredefinedMaxValue(
                    prevMonthDate.getTime() +
                        getDaysInMonth(
                            prevMonthDate.getFullYear(),
                            prevMonthDate.getMonth()
                        ) *
                            DAY +
                        tzOffset
                );
                break;
            case UIDateRange.cloudLinksTypes.PREV_MONTH_TODAY:
                this.setPredefinedMinValue(prevMonthDate.getTime());
                break;
            /* istanbul ignore next */
            default:
                break;
        }

        this.cloudLinkCallback?.();
    }

    /**
     * Helper functions to set min date.
     * @param {number} t
     * @private
     */
    private setPredefinedMinValue(t: number): void {
        if (!this.pickerFrom) {
            return;
        }
        this.pickerFrom.value = this.pickerFrom.timestampToDateString(t);
    }

    /**
     * Helper functions to set max date.
     * @param {number} t
     * @private
     */
    private setPredefinedMaxValue(t: number): void {
        if (!this.pickerTo) {
            return;
        }
        this.pickerTo.value = this.pickerTo.timestampToDateString(t);
    }

    /**
     * Checks if an id is a generated id (empty or matches Digest.randomId() pattern)
     * @param {string|null|undefined} id
     * @returns {boolean}
     * @private
     */
    private isGeneratedId(id: Optional<string>): boolean {
        return !id || /^uid-[0-9a-f]{8}$/i.test(id);
    }

    /**
     * Returns the control id that should be used by ui-field for label association.
     * For ui-daterange, returns the base id for the group element (from control-id or generated).
     * The datepickers derive their ids from this base.
     * @returns {string}
     */
    getControlIdForField(): string {
        const existingId = this.getAttribute('id') || this.id;

        if (existingId?.trim() && !this.isGeneratedId(existingId)) {
            return existingId;
        }

        if (this.controlId) {
            this.id = this.controlId;
            this.setAttribute('id', this.controlId);
            return this.controlId;
        }

        const baseId = Digest.randomId();
        this.id = baseId;
        this.setAttribute('id', baseId);
        return baseId;
    }

    /**
     * Sets the relationship between the label of an ancestor ui-field and the host.
     * @private
     */
    private updateId(): void {
        const fieldEl = this.closest<UIField>('ui-field');
        const labelEl = fieldEl?.querySelector<HTMLElement>(
            '.ui-field__label-inner'
        );

        if (!fieldEl || !labelEl) {
            return;
        }

        const groupId = this.getControlIdForField();

        if (!labelEl.id && groupId) {
            labelEl.setAttribute('id', `${groupId}-label`);
        }
        this.setAttribute('role', 'group');
        const labelledBy = labelEl.id || (groupId ? `${groupId}-label` : null);
        if (labelledBy) {
            this.setAttribute('aria-labelledby', labelledBy);
        }
    }

    /**
     * Updates derived IDs on datepicker inputs when control-id changes.
     * Does so without rebuilding the entire markup,
     * avoiding the need to call render() which bypasses the component lifecycle.
     * @private
     */
    private updateDerivedIds(): void {
        const baseId = this.getControlIdForField();
        if (baseId && this.pickerFrom?.control && !this._fromInputHadUserId) {
            this.pickerFrom.control.setAttribute('id', `${baseId}-from`);
        }
        if (baseId && this.pickerTo?.control && !this._toInputHadUserId) {
            this.pickerTo.control.setAttribute('id', `${baseId}-to`);
        }
        this.updateId();
    }

    /** @private */
    private subscribeEvents(): void {
        Array.from(this.cloudLinks).forEach((link) => {
            link.addEventListener('click', this.handleCloudLinkClick);
        });
        this.pickerFrom?.addEventListener('input', this.handleChangeDateFrom);
        this.pickerTo?.addEventListener('input', this.handleChangeDateTo);
    }

    /** @private */
    private unsubscribeEvents(): void {
        Array.from(this.cloudLinks).forEach((link) => {
            link.removeEventListener('click', this.handleCloudLinkClick);
        });
        this.pickerFrom?.removeEventListener(
            'input',
            this.handleChangeDateFrom
        );
        this.pickerTo?.removeEventListener('input', this.handleChangeDateTo);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const datePickers: Array<UIDatePicker> = [];
        const timePickers: Array<UndefinedOr<UITimePicker>> = [];
        const cloudLinksTypes = this.getCloudLinksTypes();

        Array.from(this.children).forEach((node) => {
            const isPicker = node?.localName === 'ui-datepicker';
            if (!isPicker) {
                return;
            }
            datePickers.push(node as UIDatePicker);
            const nextNode = node.nextElementSibling;
            if (nextNode?.localName === 'ui-timepicker') {
                timePickers[datePickers.length - 1] = nextNode as UITimePicker;
            }
        });
        const pickerTimeInputs: Array<NullOr<HTMLInputElement>> = [
            timePickers[0]
                ? timePickers[0].querySelector<HTMLInputElement>('input')
                : null,
            timePickers[1]
                ? timePickers[1].querySelector<HTMLInputElement>('input')
                : null,
        ];
        if (pickerTimeInputs[0]) {
            pickerTimeInputs[0].setAttribute(
                'aria-label',
                UIDateRange.labels.TIME_START as string
            );
        }
        if (pickerTimeInputs[1]) {
            pickerTimeInputs[1].setAttribute(
                'aria-label',
                UIDateRange.labels.TIME_END as string
            );
        }
        detachChildNodes(this);

        const pickerDateInputs: Array<NullOr<HTMLInputElement>> = [
            datePickers[0]
                ? datePickers[0].querySelector<HTMLInputElement>('input')
                : null,
            datePickers[1]
                ? datePickers[1].querySelector<HTMLInputElement>('input')
                : null,
        ];

        const fromId = pickerDateInputs[0]?.getAttribute('id');
        const toId = pickerDateInputs[1]?.getAttribute('id');
        this._fromInputHadUserId = !!(
            pickerDateInputs[0] &&
            fromId?.trim() &&
            !this.isGeneratedId(fromId)
        );
        this._toInputHadUserId = !!(
            pickerDateInputs[1] &&
            toId?.trim() &&
            !this.isGeneratedId(toId)
        );

        const hostId = this.getAttribute('id') || this.id;
        const hostHadUserId = !!(hostId?.trim() && !this.isGeneratedId(hostId));

        // Base id for deriving datepicker input ids
        const baseId = hostHadUserId
            ? hostId
            : this.controlId || Digest.randomId();

        if (!hostHadUserId) {
            this.id = baseId;
            this.setAttribute('id', baseId);
        }

        if (pickerDateInputs[0]) {
            pickerDateInputs[0].setAttribute(
                'aria-label',
                UIDateRange.labels.PERIOD_START as string
            );
            // Wait for ui-datepicker to be defined before accessing button
            void customElements.whenDefined('ui-datepicker').then(() => {
                datePickers[0]?.button?.setAttribute(
                    'aria-label',
                    UIDateRange.labels.CHOOSE_DATE_START as string
                );
            });
        }
        if (pickerDateInputs[1]) {
            pickerDateInputs[1].setAttribute(
                'aria-label',
                UIDateRange.labels.PERIOD_END as string
            );
            void customElements.whenDefined('ui-datepicker').then(() => {
                datePickers[1]?.button?.setAttribute(
                    'aria-label',
                    UIDateRange.labels.CHOOSE_DATE_END as string
                );
            });
        }

        pickerDateInputs
            .filter((input) => !!input)
            .forEach((input) => {
                updateElement(input, {
                    attributes: {
                        min: this.min,
                        max: this.max,
                    },
                });
            });

        insertElements(this, [
            {
                tagName: 'div',
                classList: {
                    'ui-daterange__pickers': true,
                },
                children: [
                    {
                        tagName: 'ui-datepicker',
                        element: datePickers[0],
                        attributes: {
                            'allow-empty':
                                this.allowEmpty ||
                                datePickers[0]?.allowEmpty ||
                                null,
                        },
                        classList: {
                            '-from': true,
                        },
                        children: [
                            !pickerDateInputs[0]
                                ? {
                                      tagName: 'input',
                                      attributes: {
                                          'id': `${baseId}-from`,
                                          'aria-label':
                                              UIDateRange.labels.PERIOD_START,
                                          'min': this.min,
                                          'max': this.max,
                                      },
                                  }
                                : null,
                        ],
                    },
                    timePickers[0] || null,
                ],
            },
            {
                tagName: 'span',
                classList: {
                    '-field-range-separator': true,
                },
            },
            {
                tagName: 'div',
                classList: {
                    'ui-daterange__pickers': true,
                },
                children: [
                    {
                        tagName: 'ui-datepicker',
                        element: datePickers[1],
                        attributes: {
                            'allow-empty':
                                this.allowEmpty ||
                                datePickers[1]?.allowEmpty ||
                                null,
                        },
                        classList: {
                            '-to': true,
                        },
                        children: [
                            !pickerDateInputs[1]
                                ? {
                                      tagName: 'input',
                                      attributes: {
                                          'id': `${baseId}-to`,
                                          'aria-label':
                                              UIDateRange.labels.PERIOD_END,
                                          'min': this.min,
                                          'max': this.max,
                                      },
                                  }
                                : null,
                        ],
                    },
                    timePickers[1] || null,
                ],
            },
        ]);

        updateClassList(this, {
            '-timepick': timePickers.length > 0,
        });
        this.syncControls();

        if (cloudLinksTypes.length !== 0) {
            this.appendChild(this.buildCloudLinks(cloudLinksTypes));
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.updateDerivedIds();
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
